(function () {
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getPerPage() {
    if (window.matchMedia('(max-width: 749px)').matches) return 1;
    if (window.matchMedia('(max-width: 989px)').matches) return 2;
    return 3;
  }

  function renderReview(review) {
    const article = document.createElement('article');
    article.className = 'ssw-reviews__card';
    article.setAttribute('role', 'listitem');
    article.innerHTML = `
      <blockquote class="ssw-reviews__quote">
        <p>${escapeHtml(review.text)}</p>
      </blockquote>
      <footer class="ssw-reviews__author">
        <span class="ssw-reviews__name">${escapeHtml(review.name)}</span>
      </footer>
    `;
    return article;
  }

  class ReviewsCarousel {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-ssw-reviews-viewport]');
      this.track = section.querySelector('[data-ssw-reviews-track]');
      this.prevBtn = section.querySelector('[data-ssw-reviews-prev]');
      this.nextBtn = section.querySelector('[data-ssw-reviews-next]');
      this.dotsContainer = section.querySelector('[data-ssw-reviews-dots]');
      this.currentIndex = 0;
      this.perPage = getPerPage();
      this.autoplayTimer = null;

      this.onPrev = () => this.goTo(this.currentIndex - this.perPage);
      this.onNext = () => this.goTo(this.currentIndex + this.perPage);
      this.onResize = () => {
        const previousPerPage = this.perPage;
        this.perPage = getPerPage();
        this.section.style.setProperty('--ssw-reviews-per-page', this.perPage);
        if (previousPerPage !== this.perPage) {
          this.buildDots();
        }
        this.goTo(this.currentIndex, false);
      };

      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize);

      this.section.addEventListener('mouseenter', () => this.stopAutoplay());
      this.section.addEventListener('mouseleave', () => this.startAutoplay());
      this.section.addEventListener('focusin', () => this.stopAutoplay());
      this.section.addEventListener('focusout', () => this.startAutoplay());

      let touchStartX = 0;
      this.viewport?.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.changedTouches[0].screenX;
        },
        { passive: true }
      );
      this.viewport?.addEventListener(
        'touchend',
        (event) => {
          const diff = event.changedTouches[0].screenX - touchStartX;
          if (Math.abs(diff) < 40) return;
          if (diff > 0) {
            this.onPrev();
          } else {
            this.onNext();
          }
        },
        { passive: true }
      );
    }

    get cards() {
      return Array.from(this.track?.querySelectorAll('.ssw-reviews__card:not(.ssw-reviews__card--placeholder)') || []);
    }

    get maxStartIndex() {
      return Math.max(0, this.cards.length - this.perPage);
    }

    get totalPages() {
      if (!this.cards.length) return 0;
      return Math.ceil(this.cards.length / this.perPage);
    }

    getCurrentPage() {
      return Math.min(this.totalPages - 1, Math.floor(this.currentIndex / this.perPage));
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';

      for (let page = 0; page < this.totalPages; page += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ssw-reviews__dot';
        dot.setAttribute('aria-label', `Show review page ${page + 1}`);
        dot.addEventListener('click', () => this.goToPage(page));
        this.dotsContainer.appendChild(dot);
      }

      this.updateDots();
    }

    updateDots() {
      if (!this.dotsContainer) return;
      const activePage = this.getCurrentPage();
      this.dotsContainer.querySelectorAll('.ssw-reviews__dot').forEach((dot, index) => {
        const isActive = index === activePage;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    updateButtons() {
      const atStart = this.currentIndex <= 0;
      const atEnd = this.currentIndex >= this.maxStartIndex;
      this.prevBtn?.toggleAttribute('disabled', atStart);
      this.nextBtn?.toggleAttribute('disabled', atEnd);
    }

    goToPage(page) {
      this.goTo(page * this.perPage);
    }

    goTo(index, animate = true) {
      if (!this.track || !this.cards.length) return;

      this.currentIndex = Math.max(0, Math.min(index, this.maxStartIndex));

      const card = this.cards[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const offset = this.currentIndex * (card.offsetWidth + gap);

      if (!animate) {
        this.track.style.transition = 'none';
      }

      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;

      if (!animate) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }

      this.updateButtons();
      this.updateDots();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.cards.length <= this.perPage) return;

      this.autoplayTimer = window.setInterval(() => {
        if (this.currentIndex >= this.maxStartIndex) {
          this.goTo(0);
        } else {
          this.goTo(this.currentIndex + this.perPage);
        }
      }, 6000);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    destroy() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
    }
  }

  async function initSection(section) {
    const track = section.querySelector('[data-ssw-reviews-track]');
    const url = section.dataset.reviewsUrl;
    if (!track || !url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load reviews');

      const data = await response.json();
      const reviews = (data.reviews || []).filter((review) => review.enabled !== false);

      track.innerHTML = '';
      reviews.forEach((review) => {
        track.appendChild(renderReview(review));
      });

      section.classList.add('is-loaded');

      const carousel = new ReviewsCarousel(section);
      carousel.perPage = getPerPage();
      section.style.setProperty('--ssw-reviews-per-page', carousel.perPage);
      carousel.buildDots();
      carousel.goTo(0, false);
      carousel.startAutoplay();
      section._sswReviewsCarousel = carousel;
    } catch (error) {
      section.classList.add('is-error');
      track.innerHTML = '';
    }
  }

  function init() {
    document.querySelectorAll('[data-ssw-reviews]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
